import { useEffect, useState, useRef } from "react";
import { Eye, EyeOff, Trash2, Plus, X, GripVertical, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BeforeAfter from "@/components/BeforeAfter";
import PublishShowcase from "@/components/PublishShowcase";
import BulkUploadPairs from "@/components/BulkUploadPairs";
import { Stars } from "@/components/ReviewCard";

function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const load = async () => {
    const { data } = await api.get("/reviews/all");
    setReviews(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = async (r) => {
    await api.patch(`/reviews/${r.id}`, { published: !r.published });
    await load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/reviews/${id}`);
    await load();
  };
  return (
    <div className="mt-14" data-testid="admin-reviews">
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Reviews</div>
      <h2 className="font-display mt-2 text-2xl sm:text-3xl font-extrabold uppercase text-slate-100">Customer ratings</h2>
      {reviews.length === 0 && <div className="cj-card mt-5 p-5 text-sm text-slate-400">No reviews yet — customers get a review link by email when you mark a booking completed.</div>}
      <div className="mt-5 grid gap-3">
        {reviews.map((r) => (
          <div key={r.id} className="cj-card p-4 flex flex-wrap items-start justify-between gap-4" data-testid={`admin-review-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <Stars n={r.rating} />
                <span className="font-display uppercase font-bold text-slate-100">{r.customer_name}</span>
                <span className={`cj-pill border ${r.published ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-slate-400 border-white/10"}`}>{r.published ? "Live" : "Hidden"}</span>
              </div>
              {r.quote && <p className="text-sm text-slate-300 mt-2">“{r.quote}”</p>}
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{[r.vehicle_label, r.service_name].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(r)} data-testid={`admin-review-toggle-${r.id}`} className="cj-btn-ghost px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1">
                {r.published ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Publish</>}
              </button>
              <button onClick={() => del(r.id)} data-testid={`admin-review-delete-${r.id}`} className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const load = async () => {
    const { data } = await api.get("/gallery/all");
    setItems(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (it) => {
    await api.patch(`/gallery/${it.id}`, { published: !it.published });
    await load();
  };
  const del = async (id) => {
    if (!window.confirm("Remove this pair from the showcase?")) return;
    await api.delete(`/gallery/${id}`);
    await load();
  };

  const onDrop = async (dropIdx) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from === null || from === dropIdx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    setItems(next); // optimistic
    try {
      await api.post("/gallery/reorder", { ordered_ids: next.map((x) => x.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error("Could not save order");
      await load();
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Showcase</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">Before / After gallery</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setBulk(true)} data-testid="admin-gallery-bulk-button" className="cj-btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2">
            <Layers className="h-4 w-4" /> Bulk upload
          </button>
          <button onClick={() => setAdding(true)} data-testid="admin-gallery-add-button" className="cj-btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> New pair
          </button>
        </div>
      </div>

      {items.length === 0 && <div className="cj-card mt-6 p-6 text-slate-400 text-sm">Nothing published yet. Open a booking and hit "Publish to gallery", or add a pair here.</div>}

      {items.length > 1 && (
        <p className="mt-6 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Drag the <GripVertical className="inline h-3.5 w-3.5 -mt-0.5" /> handle to set the order shown on your site.</p>
      )}

      <div className="mt-4 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {items.map((it, idx) => (
          <div
            key={it.id}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(idx); }}
            onDrop={() => onDrop(idx)}
            className={`cj-card p-3 transition ${overIndex === idx ? "ring-2 ring-[#ff6b00]" : ""}`}
            data-testid={`admin-gallery-item-${it.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <button
                draggable
                onDragStart={() => { dragIndex.current = idx; }}
                onDragEnd={() => { dragIndex.current = null; setOverIndex(null); }}
                data-testid={`admin-gallery-drag-${it.id}`}
                className="cursor-grab active:cursor-grabbing h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400"
                title="Drag to reorder"
              >
                <GripVertical className="h-4 w-4" />
              </button>
              <span className="font-mono text-[10px] text-slate-500">#{idx + 1}</span>
            </div>
            <BeforeAfter item={it} className="aspect-[4/3]" />
            <div className="mt-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display uppercase font-bold text-slate-100 truncate">{it.caption || "Untitled"}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5 truncate">
                  {[it.vehicle_label, it.service_name].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <span className={`cj-pill border shrink-0 ${it.published ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-slate-400 border-white/10"}`}>
                {it.published ? "Live" : "Hidden"}
              </span>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => toggle(it)} data-testid={`admin-gallery-toggle-${it.id}`} className="cj-btn-ghost flex-1 px-3 py-2 rounded-lg text-xs inline-flex items-center justify-center gap-1">
                {it.published ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Publish</>}
              </button>
              <button onClick={() => del(it.id)} data-testid={`admin-gallery-delete-${it.id}`} className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ReviewsManager />

      {adding && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setAdding(false)}>
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setAdding(false)} data-testid="admin-gallery-add-close" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <PublishShowcase booking={null} onPublished={() => { setAdding(false); load(); }} />
          </div>
        </div>
      )}

      {bulk && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setBulk(false)}>
          <div className="w-full max-w-2xl mt-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setBulk(false)} data-testid="admin-gallery-bulk-close" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <BulkUploadPairs onPublished={load} />
          </div>
        </div>
      )}
    </div>
  );
}
